import React, { useState, useEffect, useRef } from "react";
import axios from "../../../utils/axios";
import { useNavigate } from "react-router-dom";
import {
  FiChevronLeft,
  FiSend,
  FiImage,
  FiVideo,
  FiFile,
} from "react-icons/fi";

// ── Country codes ─────────────────────────────────────────────────────────────
const COUNTRIES = [
  { code: "+91", label: "🇮🇳 +91  India" },
  { code: "+1", label: "🇺🇸 +1   USA" },
  { code: "+44", label: "🇬🇧 +44  UK" },
  { code: "+971", label: "🇦🇪 +971 UAE" },
  { code: "+61", label: "🇦🇺 +61  Australia" },
  { code: "+49", label: "🇩🇪 +49  Germany" },
  { code: "+33", label: "🇫🇷 +33  France" },
  { code: "+81", label: "🇯🇵 +81  Japan" },
  { code: "+86", label: "🇨🇳 +86  China" },
  { code: "+55", label: "🇧🇷 +55  Brazil" },
];

// ── WhatsApp Phone Preview (exact same as CreateTemplate) ─────────────────────
function WhatsAppPreview({ template, varValues }) {
  const renderMessage = (text) => {
    if (!text) return null;
    let rendered = text;
    (template?.variables || []).forEach((v) => {
      rendered = rendered
        .split(`{{${v.num}}}`)
        .join(varValues[v.num] || v.sample || `{{${v.num}}}`);
    });
    rendered = rendered.replace(/\*(.*?)\*/g, "<strong>$1</strong>");
    rendered = rendered.replace(/_(.*?)_/g, "<em>$1</em>");
    rendered = rendered.replace(/~(.*?)~/g, "<del>$1</del>");
    return <span dangerouslySetInnerHTML={{ __html: rendered }} />;
  };

  const hasContent =
    template &&
    (template.header ||
      template.message ||
      template.footer ||
      template.templateType === "Text with Media");

  return (
    <div className="sticky top-6">
      <div className="mb-4">
        <p className="font-semibold text-gray-800 text-base mb-2">
          Template Preview
        </p>
        <div className="space-y-1 text-xs text-gray-600">
          <p>
            Name:{" "}
            <span className="font-medium text-gray-800">
              {template?.templateName || "—"}
            </span>
          </p>
          <p>
            Language:{" "}
            <span className="font-semibold text-teal-700">
              {template?.language || "—"}
            </span>
          </p>
          <p>
            Category:{" "}
            <span className="font-semibold text-teal-700">
              {template?.category || "—"}
            </span>
          </p>
          <p>
            Type:{" "}
            <span className="font-semibold text-teal-700">
              {template?.templateType || "—"}
            </span>
          </p>
        </div>
      </div>

      <div className="relative mx-auto" style={{ width: 260 }}>
        <div className="bg-gray-900 rounded-[36px] p-2 shadow-2xl">
          <div
            className="bg-white rounded-[28px] overflow-hidden"
            style={{ height: 500 }}
          >
            {/* WA header bar */}
            <div className="bg-teal-700 px-3 py-2 flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center text-xs font-bold text-teal-800">
                J
              </div>
              <div>
                <p className="text-white text-xs font-semibold leading-tight">
                  jaiswal softwar...
                </p>
                <p className="text-teal-200 text-[10px]">online</p>
              </div>
            </div>

            {/* Chat area */}
            <div
              className="p-3 flex flex-col gap-2 overflow-y-auto"
              style={{
                height: 382,
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none'%3E%3Cg fill='%23d1fae5' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundColor: "#e5ddd5",
              }}
            >
              {hasContent ? (
                <div className="bg-white rounded-lg shadow-sm max-w-[90%] overflow-hidden self-start">
                  {/* Media */}
                  {template.templateType === "Text with Media" &&
                    (template.mediaUrl && template.mediaType === "image" ? (
                      <img
                        src={template.mediaUrl}
                        alt="media"
                        className="w-full max-h-28 object-cover"
                      />
                    ) : (
                      <div className="bg-gray-100 h-20 flex items-center justify-center">
                        <div className="text-center text-gray-400">
                          {template.mediaType === "video" ? (
                            <FiVideo size={20} className="mx-auto" />
                          ) : template.mediaType === "document" ? (
                            <FiFile size={20} className="mx-auto" />
                          ) : (
                            <FiImage size={20} className="mx-auto" />
                          )}
                          <span className="text-[9px] mt-1 block capitalize">
                            {template.mediaType || "Media"}
                          </span>
                        </div>
                      </div>
                    ))}
                  {/* Header */}
                  {template.header && (
                    <div className="px-2.5 pt-2 pb-0.5">
                      <p className="text-xs font-bold text-gray-800">
                        {template.header}
                      </p>
                    </div>
                  )}
                  {/* Body */}
                  {template.message && (
                    <div className="px-2.5 py-1.5">
                      <p className="text-xs text-gray-700 leading-relaxed">
                        {renderMessage(template.message)}
                      </p>
                    </div>
                  )}
                  {/* Footer */}
                  {template.footer && (
                    <div className="px-2.5 pb-1">
                      <p className="text-[10px] text-gray-400">
                        {template.footer}
                      </p>
                    </div>
                  )}
                  <div className="px-2.5 pb-1.5 text-right">
                    <span className="text-[9px] text-gray-300">
                      10:30 AM ✓✓
                    </span>
                  </div>
                  {/* Buttons */}
                  {template.buttons?.filter((b) => b.text).length > 0 && (
                    <div className="border-t border-gray-100">
                      {template.buttons
                        .filter((b) => b.text)
                        .map((btn, i) => (
                          <div
                            key={i}
                            className="text-center py-1.5 text-xs font-medium border-b border-gray-100 last:border-0 text-blue-500"
                          >
                            {btn.type === "url"
                              ? "🔗 "
                              : btn.type === "phone"
                                ? "📞 "
                                : ""}
                            {btn.text}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex h-full items-center justify-center">
                  <p className="text-[11px] text-gray-400 text-center px-4">
                    Select a template to see the preview
                  </p>
                </div>
              )}
            </div>

            {/* WA bottom bar */}
            <div className="bg-gray-100 px-2 py-1.5 flex items-center gap-1.5">
              <div className="flex-1 bg-white rounded-full px-3 py-1">
                <p className="text-[10px] text-gray-300">Type a message</p>
              </div>
              <div className="w-6 h-6 bg-teal-600 rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function SendWhatsapp() {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [country, setCountry] = useState("+91");
  const [phone, setPhone] = useState("");
  const [varValues, setVarValues] = useState({});
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  // fetch approved templates on mount
  useEffect(() => {
    const load = async () => {
      setLoadingTemplates(true);
      try {
        const res = await axios.get(
          "/api/whatsapp/templates?status=Approved&limit=100",
        );
        if (res.data.success) setTemplates(res.data.data);
        else showToast("Failed to load templates", "error");
      } catch {
        showToast("Network error fetching templates", "error");
      } finally {
        setLoadingTemplates(false);
      }
    };
    load();
  }, []);

  const handleTemplateChange = (e) => {
    const id = e.target.value;
    const t = templates.find((x) => x._id === id) || null;
    setSelectedTemplate(t);
    setVarValues({});
  };

  const showToast = (msg, type = "success") => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, type });
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  };

  const handleSend = async () => {
    if (!selectedTemplate)
      return showToast("Please select a template", "error");
    if (!phone.trim()) return showToast("Enter a phone number", "error");
    if (!/^\d{7,15}$/.test(phone.trim()))
      return showToast("Invalid phone number", "error");

    if (selectedTemplate.dynamicContent) {
      for (const v of selectedTemplate.variables || []) {
        if (!varValues[v.num]?.trim())
          return showToast(`Please fill value for {{${v.num}}}`, "error");
      }
    }

    setSending(true);
    try {
      const res = await axios.post("/api/whatsapp/send-single", {
        templateId: selectedTemplate._id,
        to: country + phone.trim(),
        variables: varValues,
      });
      if (res.data.success) {
        showToast("Message sent successfully! ✓", "success");
        setPhone("");
        setVarValues({});
      } else {
        showToast(res.data.error || "Failed to send", "error");
      }
    } catch (err) {
      showToast(err?.response?.data?.error || "Network error", "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Top bar ── */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-sm"
          >
            <FiChevronLeft size={16} /> Back
          </button>
          <span className="text-gray-300">|</span>
          <h1 className="text-base font-semibold text-gray-800">
            Send WhatsApp Message
          </h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 flex gap-6">
        {/* ── LEFT: Form ── */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* Card 1 — Template Selection */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="bg-teal-700 text-white px-4 py-2.5 flex items-center justify-between">
              <span className="text-sm font-semibold">Select Template</span>
              <span className="text-lg leading-none">−</span>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  WhatsApp Template{" "}
                  <span className="text-xs text-gray-400">(Approved only)</span>
                </label>
                {loadingTemplates ? (
                  <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                ) : (
                  <select
                    onChange={handleTemplateChange}
                    defaultValue=""
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                  >
                    <option value="" disabled>
                      — Select an approved template —
                    </option>
                    {templates.length === 0 && (
                      <option disabled>No approved templates found</option>
                    )}
                    {templates.map((t) => (
                      <option key={t._id} value={t._id}>
                        {t.templateName} · {t.language} · {t.category}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Template meta chips */}
              {selectedTemplate && (
                <div className="flex flex-wrap gap-2">
                  {[
                    {
                      label: selectedTemplate.category,
                      cls: "bg-blue-50 text-blue-700 border-blue-100",
                    },
                    {
                      label: selectedTemplate.language,
                      cls: "bg-teal-50 text-teal-700 border-teal-100",
                    },
                    {
                      label: selectedTemplate.templateType,
                      cls: "bg-purple-50 text-purple-700 border-purple-100",
                    },
                    {
                      label: "✓ Approved",
                      cls: "bg-green-100 text-green-700 border-green-200",
                    },
                  ].map((chip) => (
                    <span
                      key={chip.label}
                      className={`text-xs font-medium px-2.5 py-1 rounded-full border ${chip.cls}`}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Card 2 — Variables (only when needed) */}
          {selectedTemplate?.dynamicContent &&
            selectedTemplate.variables?.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="bg-teal-700 text-white px-4 py-2.5 flex items-center justify-between">
                  <span className="text-sm font-semibold">
                    Fill Template Variables
                  </span>
                  <span className="text-lg leading-none">−</span>
                </div>
                <div className="p-4">
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 space-y-2">
                    <p className="text-xs font-medium text-amber-700">
                      Enter values for dynamic variables
                    </p>
                    {selectedTemplate.variables.map((v) => (
                      <div key={v.num} className="flex items-center gap-2">
                        <span className="text-xs bg-white border border-amber-200 text-gray-600 px-2.5 py-1.5 rounded-lg font-mono whitespace-nowrap flex-shrink-0">
                          {`{{${v.num}}}`}
                        </span>
                        <input
                          type="text"
                          placeholder={v.sample || `Value for {{${v.num}}}`}
                          value={varValues[v.num] || ""}
                          onChange={(e) =>
                            setVarValues((prev) => ({
                              ...prev,
                              [v.num]: e.target.value,
                            }))
                          }
                          className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-teal-400 bg-white"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

          {/* Card 3 — Recipient */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="bg-teal-700 text-white px-4 py-2.5 flex items-center justify-between">
              <span className="text-sm font-semibold">Recipient Details</span>
              <span className="text-lg leading-none">−</span>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Recipient Mobile Number{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-44 flex-shrink-0 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    placeholder="Enter WhatsApp number"
                    value={phone}
                    onChange={(e) =>
                      setPhone(e.target.value.replace(/\D/g, ""))
                    }
                    maxLength={15}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                {phone && (
                  <p className="text-xs text-gray-400 mt-1.5">
                    Full number:{" "}
                    <span className="font-medium text-gray-600">
                      {country} {phone}
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Send button */}
          <div className="flex justify-center gap-3 pb-6">
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !selectedTemplate || !phone}
              className="flex items-center gap-2 px-8 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-medium disabled:opacity-50 shadow-sm transition-colors"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Sending...
                </>
              ) : (
                <>
                  <FiSend size={15} />
                  Send Message 📤
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── RIGHT: Preview ── */}
        <div className="hidden lg:block w-72 flex-shrink-0">
          <WhatsAppPreview template={selectedTemplate} varValues={varValues} />
        </div>
      </div>

      {/* ── Toast ── */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg text-sm font-medium border
            ${
              toast.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
        >
          <span>{toast.type === "success" ? "✓" : "✕"}</span>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
